import React, { useEffect, useState } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
const navItems = [{
  name: 'דף הבית',
  path: '/',
  hash: '#home'
}, {
  name: 'SUP',
  path: '/sup',
  subItems: [{
    name: 'SUP Race',
    path: '/sup-race'
  }, {
    name: 'טיולי סאפ',
    path: '/sup-tours'
  }]
}, {
  name: 'קיאק Surfski',
  path: '/surfski'
}, {
  name: 'Wing Foil',
  path: '/wingfoil',
  subItems: [{
    name: 'מד רוח',
    path: '/wind-meter'
  }]
}, {
  name: 'אירועים וסדנאות',
  path: '/events',
  subItems: [{
    name: 'ריטריט RESET',
    path: '/reset-retreat'
  }]
}, {
  name: 'Water Tribe Fest',
  path: '/water-tribe-fest'
}, {
  name: 'צור קשר',
  path: '/',
  hash: '#contact'
}];
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const {
    scrollY
  } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();
  useMotionValueEvent(scrollY, 'change', latest => {
    setIsScrolled(latest > 50);
  });
  const handleNavClick = (item: (typeof navItems)[0]) => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    // If it's a hash link on the home page
    if (item.hash && location.pathname === '/') {
      const element = document.querySelector(item.hash);
      element?.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }
    // If it's a hash link but we're not on home page
    if (item.hash && location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(item.hash!);
        element?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
      return;
    }
    // For regular page navigation, scroll to top
    window.scrollTo(0, 0);
  };
  return <motion.nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isMobileMenuOpen ? 'bg-[#0A5F8F] py-2' : isScrolled ? 'bg-[#0A5F8F]/90 backdrop-blur-md shadow-lg py-2' : 'bg-transparent py-6'}`} initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-209">
      <div className="container mx-auto px-4 md:px-8" data-id="element-210">
        <div className="flex items-center justify-between" data-id="element-211">
          {/* Logo - Right side for RTL - ENLARGED */}
          <Link to="/" className="flex items-center z-50" data-id="element-212">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/m3nP7fc9XV1ZSHtYcih2QJ/Beach_Club_Logo.png" alt="Beach Club Logo" className="h-16 w-16 md:h-24 md:w-24 object-cover rounded-full border-2 border-white/20 shadow-lg" data-id="element-213" />
          </Link>

          {/* Desktop Menu - Left side for RTL */}
          <div className="hidden md:flex items-center gap-8" data-id="element-214">
            {navItems.map(item => <div key={item.name} className="relative" onMouseEnter={() => item.subItems && setOpenDropdown(item.name)} onMouseLeave={() => setOpenDropdown(null)} data-id="element-215">
                {item.subItems ? <>
                    <Link to={item.path} className="text-white/90 hover:text-white font-medium transition-colors text-base relative group flex items-center gap-1" data-id="element-216">
                      {item.name}
                      <ChevronDown className="w-4 h-4" data-id="element-217" />
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" data-id="element-218" />
                    </Link>

                    {/* Dropdown */}
                    {openDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl py-2 min-w-[200px]" data-id="element-219">
                        {item.subItems.map(subItem => <Link key={subItem.name} to={subItem.path} className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-[#0A5F8F] transition-colors text-sm font-medium" onClick={() => setOpenDropdown(null)} data-id="element-220">
                            ← {subItem.name}
                          </Link>)}
                      </motion.div>}
                  </> : <Link to={item.path} onClick={() => handleNavClick(item)} className="text-white/90 hover:text-white font-medium transition-colors text-base relative group" data-id="element-221">
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" data-id="element-222" />
                  </Link>}
              </div>)}
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden z-50 p-2 transition-colors ${isScrolled ? 'text-white' : 'text-white'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-223">
            {isMobileMenuOpen ? <X data-id="element-224" /> : <Menu data-id="element-225" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <motion.div className={`fixed inset-0 bg-[#0A5F8F] z-40 flex flex-col items-center justify-center gap-6 md:hidden ${isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} initial={{
      opacity: 0
    }} animate={{
      opacity: isMobileMenuOpen ? 1 : 0
    }} transition={{
      duration: 0.3
    }} data-id="element-226">
        {navItems.map((item, index) => <div key={item.name} className="text-center" data-id="element-227">
            <Link to={item.path} className="text-2xl font-bold text-white block" onClick={() => handleNavClick(item)} data-id="element-228">
              <motion.span initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: isMobileMenuOpen ? 0 : 20,
            opacity: isMobileMenuOpen ? 1 : 0
          }} transition={{
            delay: index * 0.1
          }} data-id="element-229">
                {item.name}
              </motion.span>
            </Link>
            {item.subItems && <div className="mt-2 space-y-2" data-id="element-230">
                {item.subItems.map((subItem, subIndex) => <Link key={subItem.name} to={subItem.path} className="text-lg text-blue-200 block" onClick={() => {
            setIsMobileMenuOpen(false);
          }} data-id="element-231">
                    <motion.span initial={{
              y: 20,
              opacity: 0
            }} animate={{
              y: isMobileMenuOpen ? 0 : 20,
              opacity: isMobileMenuOpen ? 1 : 0
            }} transition={{
              delay: index * 0.1 + (subIndex + 1) * 0.05
            }} data-id="element-232">
                      ← {subItem.name}
                    </motion.span>
                  </Link>)}
              </div>}
          </div>)}
      </motion.div>
    </motion.nav>;
}